import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import useTestNotificationTriggerApi from "hooks/notifications/useTestNotificationTriggerApi";
import { useCallback, useState } from "react";

type TestResult = {
  success: boolean;
  httpStatusCode: number | null;
  errorMessage: string | null;
  responseTimeMs: number | null;
};

interface TestApiDialogProps {
  open: boolean;
  onClose: () => void;
  triggerId?: string;
  apiUrl?: string;
}

export default function TestApiDialog({ open, onClose, triggerId, apiUrl }: TestApiDialogProps) {
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testTrigger, { loading }] = useTestNotificationTriggerApi();

  const handleSend = useCallback(async () => {
    if (!triggerId) return;
    setResult(null);
    setError(null);
    try {
      const res = await testTrigger(triggerId);
      if (res) {
        setResult(res);
      } else {
        setError("No response received from the server.");
      }
    } catch (err: any) {
      setError(err?.message ?? String(err));
    }
  }, [triggerId, testTrigger]);

  const handleClose = useCallback(() => {
    setResult(null);
    setError(null);
    onClose();
  }, [onClose]);

  const statusColor = (s: number | null) => {
    if (!s) return "default";
    if (s >= 200 && s < 300) return "success";
    if (s >= 400) return "error";
    return "warning";
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h6">Test API Trigger</MDTypography>
          <IconButton size="small" onClick={handleClose}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent dividers>
        {apiUrl && (
          <MDBox mb={2}>
            <MDTypography variant="caption" color="text" display="block" mb={0.5}>
              Request will be sent to
            </MDTypography>
            <MDBox
              sx={{
                fontFamily: "monospace",
                fontSize: "12px",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
                wordBreak: "break-all",
              }}
            >
              {apiUrl}
            </MDBox>
          </MDBox>
        )}

        {!triggerId && (
          <Alert severity="info">Save this trigger first before sending a test request.</Alert>
        )}

        {(loading || result || error) && (
          <MDBox mt={2}>
            {loading && (
              <MDBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <MDTypography variant="caption">Sending via backend…</MDTypography>
              </MDBox>
            )}

            {result && !loading && (
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDTypography variant="caption" fontWeight="medium">
                  Result
                </MDTypography>
                <Chip
                  label={result.success ? "Success" : "Failed"}
                  size="small"
                  color={result.success ? "success" : "error"}
                />
                {result.httpStatusCode && (
                  <Chip
                    label={`HTTP ${result.httpStatusCode}`}
                    size="small"
                    color={statusColor(result.httpStatusCode) as any}
                    variant="outlined"
                  />
                )}
                {result.responseTimeMs !== null && (
                  <Chip label={`${result.responseTimeMs} ms`} size="small" variant="outlined" />
                )}
                {result.errorMessage && (
                  <Alert severity="warning" sx={{ mt: 1, width: "100%" }}>
                    {result.errorMessage}
                  </Alert>
                )}
              </MDBox>
            )}

            {error && !loading && <Alert severity="error">{error}</Alert>}
          </MDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {(result || error) && !loading && (
          <MDButton
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => {
              setResult(null);
              setError(null);
            }}
          >
            Reset
          </MDButton>
        )}
        <MDButton size="small" variant="outlined" color="secondary" onClick={handleClose}>
          Close
        </MDButton>
        <MDButton
          size="small"
          variant="gradient"
          color="info"
          disabled={!triggerId || loading}
          onClick={handleSend}
        >
          <Icon sx={{ mr: 0.5 }}>send</Icon> Send Test Request
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
